/*
 * BaseNode — shared base component for all standard workflow nodes.
 * Left accent stripe design with SVG icons, category tags, and
 * refined visual hierarchy inspired by n8n / Linear / Retool.
 */

import React, { memo, useContext } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getNodeIcon } from './NodeIcons';
import { DestinationListsContext, publishNodeSummary } from '../destinationStatus';
import './BaseNode.css';

const CATEGORY_LABELS = {
  trigger: 'Trigger',
  processing: 'Processing',
  routing: 'Routing',
  action: 'Action',
  output: 'Output',
};

function getConfigSummary(type, config) {
  if (!config) return '';
  switch (type) {
    case 'trigger_upload': {
      const splitMode = config.batch_split_mode;
      if (splitMode === 'auto') return 'Manual upload · Auto-split';
      if (splitMode === 'manual') return 'Manual upload · Split review';
      return 'Manual upload';
    }
    case 'trigger_cloud_source': {
      // channel_name is a DISPLAY CACHE written alongside channel_id by the
      // config panel. The binding itself is channel_id and nothing reads the
      // name — the canvas has no channel list to resolve one against, and
      // fetching per node would put a request behind every render. Cost, stated:
      // a channel renamed after the workflow was saved shows its old name here
      // until the node is reopened.
      //
      // A node WITH a channel_id but no cached name says "Source selected"
      // rather than "No source selected" — the latter would be false, and a
      // canvas that lies about whether a trigger is configured is worse than
      // one that is vague.
      const name = config.channel_name;
      const base = name
        ? `Source: ${name}`
        : config.channel_id
          ? 'Source selected'
          : 'No source selected';
      const splitMode = config.batch_split_mode;
      if (splitMode === 'auto') return `${base} · Auto-split`;
      if (splitMode === 'manual') return `${base} · Split review`;
      return base;
    }
    case 'action_await_ocr':
    case 'action_ocr': // DEPRECATED alias — renamed to action_await_ocr
      return `${config.engine || 'Auto'}${config.fallback ? ' + fallback' : ''}`;
    case 'action_extract':
      return config.mode === 'ai_assisted' ? 'AI-Assisted' : (config.mode || 'Template');
    case 'action_classify':
      return config.method === 'template_match' ? 'Template match' : (config.method || 'Template match');
    case 'action_human_review':
      return config.instructions ? config.instructions.slice(0, 40) : 'Awaiting review';
    case 'action_set_field':
      return config.field_key ? `Set: ${config.field_key}` : 'Configure field';
    case 'action_field_mapping':
      if (config.mapping) {
        const count = Object.keys(config.mapping).length;
        return `${count} field${count !== 1 ? 's' : ''} mapped`;
      }
      return 'Configure mapping';
    case 'output_publish':
      return config.destination_id ? 'Destination configured' : 'Select destination';
    case 'output_archive':
      return config.retention_days ? `Retain ${config.retention_days}d` : 'Configure retention';
    default:
      return '';
  }
}

const BaseNode = memo(({ data, selected, type }) => {
  const { label, category, config, handles, executionStatus } = data;
  const catClass = `cat-${category || 'action'}`;
  // A Publish node says what became of its destination (deleted, switched
  // off), which only the designer's destination lists can tell it.
  const destinationLists = useContext(DestinationListsContext);
  const publish = type === 'output_publish' ? publishNodeSummary(config?.destination_id, destinationLists) : null;
  const summary = publish ? publish.text : getConfigSummary(type, config);
  const inputCount = handles?.inputs ?? 1;
  const outputCount = handles?.outputs ?? 1;
  const execClass = executionStatus ? `exec-${executionStatus}` : '';
  const IconComponent = getNodeIcon(type);

  return (
    <div className={`wf-node ${catClass} ${selected ? 'selected' : ''} ${execClass}`}>
      {executionStatus === 'completed' && <span className="wf-node-exec-badge completed">✓</span>}
      {executionStatus === 'failed' && <span className="wf-node-exec-badge failed">✕</span>}
      {executionStatus === 'running' && (
        <div className="wf-node-exec-badge running"><span className="wf-node-spinner" /></div>
      )}

      {inputCount > 0 && (
        <Handle type="target" position={Position.Top} id="in" />
      )}

      <div className="wf-node-content">
        <div className="wf-node-header">
          <span className="wf-node-icon"><IconComponent /></span>
          <span className="wf-node-label">{label || type}</span>
        </div>
        {summary && (
          <div className={`wf-node-summary${publish?.problem ? ' wf-node-summary-problem' : ''}`}>{summary}</div>
        )}
        <div className="wf-node-footer">
          <span className="wf-node-category-tag">{CATEGORY_LABELS[category] || 'Node'}</span>
        </div>
      </div>

      {outputCount > 0 && (
        <Handle type="source" position={Position.Bottom} id="out" />
      )}
    </div>
  );
});

BaseNode.displayName = 'BaseNode';

export default BaseNode;
